import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const HELP = [
  "help — this list",
  "services — what we do",
  "work — the showcase",
  "about — the studio",
  "contact — start a project",
  "go <page> — jump there (e.g. go contact)",
  "whoami — rude to ask, answered anyway",
  "pricing — how scoping works",
  "stack — what this site runs on",
  "uptime — the number that matters",
  "joke — technically funny",
  "ls — what's in here",
  "date — the current moment",
  "clear — wipe the scrollback",
  "exit — close this shell",
];

const TerminalOverlay = () => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([
    { out: "thrivehaus shell v3.0 — the machine is listening. type 'help'" },
  ]);
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "`" || e.key === "~") && !e.target.closest?.("input, textarea, select, [contenteditable]")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const timer = setTimeout(() => inputRef.current?.focus(), 90);
    return () => { clearTimeout(timer); previousFocus?.focus(); };
  }, [open]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 999999 });
  }, [history]);

  const run = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    if (cmd === "clear") {
      setHistory([]);
      return;
    }
    if (cmd === "exit") {
      setHistory((h) => [...h, { in: raw, out: "closing shell…" }]);
      setTimeout(() => setOpen(false), 350);
      return;
    }
    let out;
    if (cmd === "help") out = HELP.join("\n");
    else if (cmd === "services")
      out = "websites · seo · ai-search · automation · hosting-support\ntry: go services";
    else if (cmd === "work") out = "illustrative website concepts · no client case studies\ntry: go work";
    else if (cmd === "about") out = "better digital foundations. brighter opportunities.\ntry: go about";
    else if (cmd === "contact") out = "the studio replies personally.\ntry: go contact";
    else if (cmd === "whoami") out = "an ambitious business, probably. welcome.";
    else if (cmd === "date") out = new Date().toString();
    else if (cmd === "pricing")
      out = "projects are scoped around the requirements of each business.\ntry: go contact";
    else if (cmd === "stack")
      out = "this site: react · tailwind · three.js · cloudflare\nyours: whatever fits the job";
    else if (cmd === "uptime")
      out = "This is a decorative studio terminal. Live uptime is not reported here.\ntry: go hosting";
    else if (cmd === "ls") out = "index.html  styles.css  ambition.config  secrets.env (nice try)";
    else if (cmd === "joke") {
      const jokes = [
        "there are only 10 types of people: those who understand binary and those who don't.",
        "a sql query walks into a bar, sees two tables and asks… 'mind if i join?'",
        "why do programmers prefer dark mode? because light attracts bugs.",
        "i'd tell you a udp joke, but you might not get it.",
        "a website without monitoring is just a hope with a url.",
      ];
      out = jokes[Math.floor(Math.random() * jokes.length)];
    }
    else if (cmd === "sudo make me a website") out = "permission granted. go contact.";
    else if (cmd.startsWith("go ")) {
      const target = cmd.slice(3).trim();
      const routes = {
        home: "/",
        services: "/services",
        websites: "/services/websites",
        seo: "/services/seo",
        "ai-search": "/services/ai-search",
        "ai search": "/services/ai-search",
        automation: "/services/automation",
        integrations: "/services/automation",
        hosting: "/services/hosting-support",
        "hosting-support": "/services/hosting-support",
        work: "/services/websites",
        about: "/about",
        contact: "/contact",
      };
      if (routes[target]) {
        out = `opening ${routes[target]} …`;
        setTimeout(() => {
          navigate(routes[target]);
          setOpen(false);
        }, 450);
      } else {
        out = `unknown page '${target}' — pages: home, services, websites, seo, ai-search, automation, hosting, work, about, contact`;
      }
    } else out = `'${cmd}': command not found — try 'help'`;
    setHistory((h) => [...h, { in: raw, out }]);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="terminal-overlay"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 left-4 z-[90] w-[min(560px,92vw)] rounded-lg border border-bronze/25 bg-[#FBF7F0]/97 shadow-[0_30px_70px_-22px_rgba(75,57,38,0.32)] backdrop-blur overflow-hidden"
            role="dialog"
            aria-label="Terminal"
          >
            <div className="flex items-center gap-2 px-4 h-9 border-b border-hairline bg-[#EDE2D5]/90">
              <span className="w-2.5 h-2.5 rounded-full bg-[#BDAE9A]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#BDAE9A]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#BDAE9A]" />
              <span className="ml-3 font-mono text-[10px] tracking-[0.15em] text-muted">
                visitor@thrivehaus:~
              </span>
              <button
                data-testid="terminal-close"
                aria-label="Close terminal"
                onClick={() => setOpen(false)}
                className="ml-auto p-1 text-muted hover:text-cream transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div
              ref={bodyRef}
              data-testid="terminal-output"
              className="max-h-64 overflow-y-auto p-4 font-mono text-[11px] sm:text-xs leading-relaxed"
            >
              {history.map((h, i) => (
                <div key={i} className="mb-2">
                  {h.in && (
                    <div className="text-cream/90">
                      <span className="text-bronze mr-2">❯</span>
                      {h.in}
                    </div>
                  )}
                  {h.out && <div className="text-fog whitespace-pre-line pl-4">{h.out}</div>}
                </div>
              ))}
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  run(value);
                  setValue("");
                }}
              >
                <span className="text-bronze">❯</span>
                <input
                  ref={inputRef}
                  data-testid="terminal-input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-cream placeholder:text-muted"
                  placeholder="type a command…"
                  autoComplete="off"
                  spellCheck={false}
                />
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TerminalOverlay;
